<script setup lang="ts">
import type { LessonTutorFilters } from '../../model/lessons/interfaces'

import { faChevronDown, faChevronUp, faFilter } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { AppMultiSelect } from '@shared/ui/selects'
import { AppIcon } from '@shared/ui/icons'
import { BtnPrimary, BtnSecondary } from '@shared/ui/buttons'

import { useLessonTutorFilters } from '../../model/lessons/lesson-tutor-filters.hook'

defineOptions({
  name: 'LessonTutorFilterFields',
})

/** The tutor filters behind a fold, as the legacy modal had them: a draft that reaches the tutor list on Apply and
 *  goes back to the defaults on Clear. */
const props = withDefaults(defineProps<{
  /** The filters in force: the draft starts on them when the panel opens again. */
  initial?: LessonTutorFilters | null
  disabled?: boolean
}>(), {
  initial: null,
  disabled: false,
})

const emit = defineEmits<{
  apply: [filters: LessonTutorFilters]
  clear: []
}>()

const {
  refIsOpen,
  refCount,
  refGender,
  refSpecialFeatures,
  refAccent,
  refCategories,
  refGenderOptions,
  refSpecialFeatureOptions,
  refAccentOptions,
  refCategoryOptions,
  toggle,
  apply,
  clear,
} = useLessonTutorFilters({
  initial: props.initial,
  onApply: filters => emit('apply', filters),
  onClear: () => emit('clear'),
})
</script>

<template>
  <div :class="['lesson-tutor-filter-fields', { open: refIsOpen }]">
    <div class="lesson-tutor-filter-fields__head">
      <AppText
        name="body1-medium"
        color="slate-900"
      >
        Tutor filters
      </AppText>
      <button
        type="button"
        class="lesson-tutor-filter-fields__toggle"
        :disabled="disabled"
        :aria-expanded="refIsOpen"
        @click="toggle"
      >
        <AppIcon
          :icon="faFilter"
          size="small"
        />
        ({{ refCount }})
        <AppIcon
          :icon="refIsOpen ? faChevronUp : faChevronDown"
          size="small"
        />
      </button>
    </div>
    <div
      v-if="refIsOpen"
      class="lesson-tutor-filter-fields__body"
    >
      <AppMultiSelect
        v-model="refGender"
        :options="refGenderOptions"
        label="Gender"
        placeholder="All"
        :disabled="disabled"
        clearable
      />
      <AppMultiSelect
        v-model="refSpecialFeatures"
        :options="refSpecialFeatureOptions"
        label="Special features"
        placeholder="All"
        :disabled="disabled"
        clearable
      />
      <AppMultiSelect
        v-model="refAccent"
        :options="refAccentOptions"
        label="Accent"
        placeholder="All"
        :disabled="disabled"
        clearable
      />
      <AppMultiSelect
        v-model="refCategories"
        :options="refCategoryOptions"
        label="Categories"
        placeholder="All"
        :disabled="disabled"
        clearable
      />
      <div class="lesson-tutor-filter-fields__controls">
        <BtnPrimary
          text="Apply filters"
          :disabled="disabled"
          @click="apply"
        />
        <BtnSecondary
          text="Clear"
          color="black"
          :disabled="disabled"
          @click="clear"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss" src="./LessonTutorFilterFields.scss" />
